import type { PartDefinition } from "@rogue/rackbuilder"
import { MMS } from "../../modelPaths"
import type { StorageChainConfig, MassStorageOptions } from "../../RackTypes"
import { getBaseShelfDataForHardware, type UnitShelfData, type ShelfData, getHardwareCount } from "./hardware-counting-utils"
import { assignShelfPlacement } from "./mms-shelf-utils"
import type { BuyRequest } from "@/js/services/RogueTypes"

function removeSide(basePart: PartDefinition, direction: 'left' | 'right') {
  if (direction === 'left') {
    basePart.slots.front = []
    basePart.slots.back = []
  }

  // Clear the slots on the side we are attaching to
  Object.keys(basePart.slots).forEach((key) => {
    if (
      key.startsWith('crossmember_top_')
      || key.startsWith(`${direction}_side_`)
      || key === `${direction}_side_corner`
    ) {
      basePart.slots[key] = []
    }
  })
}

// Addon Build
export function addStorageUnitSide(
  basePart: PartDefinition,
  addonChain: StorageChainConfig[],
  direction: 'left' | 'right',
  config: MassStorageOptions,
  frontWheel: PartDefinition,
  backWheel: PartDefinition,
  rubberFeet: PartDefinition,
  buyRequest: BuyRequest
) {
  if (!addonChain.length) return

  // Clear the specified side so uprights can be reattached cleanly
  removeSide(basePart, direction)

  const is100BaseNeeded = addonChain[0].heightSelection === '100'
  const initialBaseHeight = config.height

  const baseShelves = getBaseShelfDataForHardware(
    {
      shelfOneSelection   : config.shelfOneSelection,
      shelfTwoSelection   : config.shelfTwoSelection,
      shelfThreeSelection : config.shelfThreeSelection,
      shelfFourSelection  : config.shelfFourSelection,
      shelfFiveSelection  : config.shelfFiveSelection,
      heightSelection     : config.height,
    }, is100BaseNeeded
  )

  // Switch the base out if the addon is 100"
  if (is100BaseNeeded && initialBaseHeight !== '100') {
    basePart.mesh = `${MMS}#base`
  }

  let currentBase = basePart
  let previousHeight = initialBaseHeight // Track the previous height to handle transitions

  // An array to track shelf names for hardware counting
  const allUnitsShelves: UnitShelfData[] = [{
    unit       : 0,
    unitHeight : config.height,
    shelves    : baseShelves,
  }]
  let unitCounter = 1

  addonChain.forEach((addon, index) => {
    const addonHeight = addon.heightSelection

    // When adding to the left side, the new unit's right side connects to the previous unit
    // When adding to the right side, the new unit's left side connects to the previous unit
    // The shared side should always use the taller height
    const sharedHeight = Math.max(Number(previousHeight), Number(addonHeight)).toString()

    // Remove inner uprights from the previous unit if shared upright is taller
    if (Number(addonHeight) > Number(previousHeight)) {
      if (Array.isArray(currentBase.slots.front)) {
        currentBase.slots.front = []
      }
      if (Array.isArray(currentBase.slots.back)) {
        currentBase.slots.back = []
      }
    }

    // Build the addon unit's uprights - these go into the nextBase
    const nextFrontUpright: PartDefinition = { mesh: `${MMS}#upright ${addonHeight}`, slots: {} }
    const nextBackUpright: PartDefinition = { mesh: `${MMS}#upright ${addonHeight}`, slots: {} }

    // Build the uprights for the shared connection - these are pushed to the parent's slots
    const sharedFrontUpright: PartDefinition = { mesh: `${MMS}#upright ${sharedHeight}`, slots: {} }
    const sharedBackUpright: PartDefinition = { mesh: `${MMS}#upright ${sharedHeight}`, slots: {} }

    // Use shared height for crossmember as well
    const sharedSideCrossmember: PartDefinition = { mesh: `${MMS}#${sharedHeight === '100' ? 'top crossmember' : 'welded top crossmember'}`, slots: {} }
    const endSideCrossmember: PartDefinition = { mesh: `${MMS}#${addonHeight === '100' ? 'top crossmember' : 'welded top crossmember'}`, slots: {} }

    const corner: PartDefinition = { mesh: `${MMS}#corner`, slots: {} }

    // Always clear previous add-on's side before attaching new one
    removeSide(currentBase, direction)

    const addonIs100 = addon.heightSelection === '100'
    const baseIsShort = currentBase.mesh === `${MMS}#welded base` // has a 43" or a 70" upright

    // If current base part has a 43 or 70" rack but we're adding a 100" rack, swap the base to support the 100" upright
    if (addonIs100 && baseIsShort) {
      currentBase.mesh = `${MMS}#base`
    }

    // Build the other-side base
    const nextBase: PartDefinition = {
      mesh  : addon.heightSelection === '100' ? `${MMS}#base` : `${MMS}#welded base`,
      slots : {
        front                               : [nextFrontUpright],
        back                                : [nextBackUpright],
        [`crossmember_top_${addonHeight}h`] : [endSideCrossmember],
        feet                                : addon.footSelection === 'feet' ? rubberFeet : [],
        front_wheel                         : addon.footSelection === 'wheels' ? frontWheel : [],
        back_wheel                          : addon.footSelection === 'wheels' ? backWheel : [],
      },
      anchor: addon.cornerQuantity ? 'corner' : '',
    }

    // Push the shared uprights onto the parent's arrays
    // These uprights must use the shared height to properly connect units of different heights
    if (!Array.isArray(currentBase.slots.front)) currentBase.slots.front = []
    const frontCurrentUprightSlot = currentBase.slots.front as PartDefinition[]
    frontCurrentUprightSlot.push(sharedFrontUpright)

    if (!Array.isArray(currentBase.slots.back)) currentBase.slots.back = []
    const backCurrentUprightSlot = currentBase.slots.back as PartDefinition[]
    backCurrentUprightSlot.push(sharedBackUpright)

    // Use the shared height for the crossmember slot
    currentBase.slots[`crossmember_top_${sharedHeight}h`] ??= []
    const currentCrossmemberSlot = currentBase.slots[`crossmember_top_${sharedHeight}h`] as PartDefinition[]
    currentCrossmemberSlot.push(sharedSideCrossmember)

    // Apply feet or wheels directly to current base
    currentBase.slots.feet = addon.footSelection === 'feet' ? rubberFeet : frontWheel

    // Addon Corners
    if (addon.cornerQuantity) {
      const shelfKeys = ['shelfOneSelection', 'shelfTwoSelection', 'shelfThreeSelection', 'shelfFourSelection'] as const
      const slotNames = ['corner_one', 'corner_two', 'corner_three', 'corner_four']

      const cornerShelves: ShelfData[] = []

      for (let i = 0; i < Number(addon.cornerQuantity); i += 1) {
        const shelfKey = shelfKeys[i]
        const slotName = slotNames[i]
        const isCornerSelected = addon[shelfKey] === 'corner'

        // For counting hardware
        if (isCornerSelected) {
          cornerShelves.push({ hardwareSlotName: 'corner', shelfType: 'corner' })
        }

        const isHeightValid = (slotName === 'corner_three' && ['70', '100'].includes(addon.heightSelection))
            || (slotName === 'corner_four' && addon.heightSelection === '100')
            || (slotName === 'corner_one' || slotName === 'corner_two')

        if (isHeightValid) {
          currentBase.slots[slotName] = isCornerSelected ? corner : []
        } else {
          currentBase.slots[slotName] = []
        }
      }

      // For counting hardware
      if (cornerShelves.length > 0) {
        allUnitsShelves.push({
          unit       : unitCounter,
          unitHeight : addon.heightSelection,
          shelves    : cornerShelves,
        })
        unitCounter += 1
      }

      if (direction === 'right') {
        corner.anchor = 'left'
        corner.adjustments = [{ axis: 'Y', type: 'rotate', amount: 180 }, { axis: 'Y', type: 'rotate', amount: 90 }]
        nextBase.adjustments = [{ axis: 'Y', type: 'rotate', amount: 180 }, { axis: 'Y', type: 'rotate', amount: 90 }]
      }
    } else {
      // Addon Shelves
      const shelfTarget = direction === 'right' ? currentBase : nextBase

      const prevHeight = index === 0 ? config.height : addonChain[index - 1].heightSelection
      const nextHeight = addonChain[index + 1]?.heightSelection
      const isAdjacentTo100 =  prevHeight === '100' || addon.heightSelection === '100' || nextHeight === '100'

      // @TODO this line causes the error switch back from corner to  a normal rack width
      const shelvesForThisUnit = assignShelfPlacement(shelfTarget, addon, config, isAdjacentTo100)

      allUnitsShelves.push({ unit: unitCounter, unitHeight: addon.heightSelection, shelves: shelvesForThisUnit })
      unitCounter += 1
    }

    // Attach the child on the correct side
    if (addon.cornerQuantity) {
      currentBase.slots[`${direction}_side_corner`] = nextBase
    } else {
      currentBase.slots[`${direction}_side_${addon.widthSelection}h`] = nextBase
    }

    // Shift base and track previous height for the next addon chain
    currentBase = nextBase
    previousHeight = addon.heightSelection
  })

  // Add additional logic for hardware counting
  for (let i = 0; i < allUnitsShelves.length; i += 1) {
    const unit = allUnitsShelves[i]

    const isCorner = unit.shelves.every(s => s.hardwareSlotName === 'corner')

    // Mark first and last non-corner units as end units
    if (!isCorner) {
      const prev = allUnitsShelves[i - 1]
      const next = allUnitsShelves[i + 1]

      const isFirstNonCorner = !prev || prev.shelves.every(s => s.hardwareSlotName === 'corner')
      const isLastNonCorner = !next || next.shelves.every(s => s.hardwareSlotName === 'corner')

      unit.isEndUnit = isFirstNonCorner || isLastNonCorner

      // If either neighbor is a corner, mark as adjacent
      unit.adjacentToCorner = !!(prev && prev.shelves.some(s => s.hardwareSlotName === 'corner'))
                            || !!(next && next.shelves.some(s => s.hardwareSlotName === 'corner'))
    }
  }

  const result = getHardwareCount(allUnitsShelves)

  // Add the hardwareCount to the BR
  if (buyRequest.children?.[0]?.metadata) {
    buyRequest.children[0].metadata.hardwareCount ??= {}
    buyRequest.children[0].metadata.hardwareCount = result
  }
}
